import {
  BI_ORIGINS,
  hasPricingOptions,
  hasSinglePricingOption,
  isAvailableDonationTicket,
  saleScheduled,
} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {Divider} from '@wix/wix-ui-tpa/cssVars'
import {useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React from 'react'
import {useAppActions, useAppSelector} from '../../../../hooks/runtime'
import {getSelectedPlaceId} from '../../../../selectors/seating/mode'
import {isPlaceArea} from '../../../../selectors/seating/place'
import {PriceIcon, TicketIcon} from '../../../common/icons'
import {ViewButton} from '../plan/view-button'
import {StyledButton} from '../styled-button'
import {Summary} from '../summary'
import {TicketAction} from '../ticket/ticket-action'
import {TicketInfo} from '../ticket/ticket-info'
import {classes} from './footer.st.css'
import {FooterProps} from './interfaces'

export const Footer = ({
  selectedPlace,
  withoutBreakdown,
  inDrawer,
  onCloseClick,
  onCheckoutClick,
  onPricesOverlayOpen,
  showAccessibilityMode,
}: FooterProps) => {
  const {t} = useTranslation()

  const ticket = selectedPlace?.ticket
  const scheduled = ticket ? saleScheduled(ticket) : undefined
  const isPlaceSelected = useAppSelector(getSelectedPlaceId) === selectedPlace?.id
  const showAddToBasket = !scheduled && inDrawer && isPlaceSelected

  const area = selectedPlace ? isPlaceArea(selectedPlace) : undefined
  const donationTicket = ticket ? isAvailableDonationTicket(ticket) : undefined
  const pricingOptions = ticket ? hasPricingOptions(ticket) : undefined
  const singlePricingOption = ticket ? hasSinglePricingOption(ticket) : undefined
  const {
    setShowAccessibilityMode,
    setShowPlaceDescription,
    resetAreaCounterValue,
    setShowPricingOptionsPlace,
    setPlaceQuantity,
  } = useAppActions()

  const handleOnMoreInfoClick = () => {
    setShowPlaceDescription(selectedPlace)
    resetAreaCounterValue({place: selectedPlace, keepQuantityIfNeeded: true})
  }

  const handleAddToBasketClick = () => {
    if (pricingOptions && !singlePricingOption) {
      setShowPricingOptionsPlace({place: selectedPlace, origin: BI_ORIGINS.MAP})
    } else if (area || donationTicket) {
      handleOnMoreInfoClick()
    } else {
      setPlaceQuantity({
        placeId: selectedPlace.id,
        count: 1,
        origin: BI_ORIGINS.MAP,
        validateDonation: false,
      })
    }
  }

  let addButtonText

  if (area || donationTicket || (pricingOptions && !singlePricingOption)) {
    addButtonText = t('seatings_continue')
  } else {
    addButtonText = t('seatings_add')
  }

  return (
    <>
      <div className={classes.mobileFooter} data-hook={DH.SEATINGS_FOOTER}>
        <div className={classes.viewButtons}>
          <ViewButton
            dataHook={DH.ACCESSIBILITY_MODE_BUTTON}
            onClick={() => setShowAccessibilityMode(true)}
            prefixIcon={<TicketIcon />}
            label={t('seatingMapModal_listTab_title')}
          />
          <ViewButton
            dataHook={DH.PRICES_OVERLAY}
            onClick={onPricesOverlayOpen}
            prefixIcon={<PriceIcon />}
            label={t('seatingMapModal_prices_title')}
          />
        </div>
        {selectedPlace ? (
          <>
            <div className={classNames(classes.container, classes.scrollable)}>
              <TicketInfo
                place={selectedPlace}
                selected={true}
                showAccessibilityMode={showAccessibilityMode}
                inDrawer={inDrawer}
                onCloseClick={onCloseClick}
              />
              <TicketAction
                onTicketMoreInfoClick={handleOnMoreInfoClick}
                place={selectedPlace}
                inDrawer={inDrawer}
                showAccessibilityMode={showAccessibilityMode}
                origin={BI_ORIGINS.SEATINGS_MODAL_BASKET}
              />
            </div>
            {showAddToBasket ? (
              <>
                <div className={classes.dividerContainer}>
                  <Divider className={classes.divider} />
                </div>
                <div className={classes.styledButtonContainer}>
                  <StyledButton
                    key="add-to-basket-button"
                    className={classes.styledButton}
                    dataHook={DH.ADD}
                    onClick={handleAddToBasketClick}
                  >
                    {addButtonText}
                  </StyledButton>
                </div>
              </>
            ) : null}
          </>
        ) : (
          <div className={classes.container}>
            <Summary withoutBreakdown={withoutBreakdown} onCheckoutClick={onCheckoutClick} />
          </div>
        )}
      </div>
    </>
  )
}
